import { Button, Container, Grid, IconButton, makeStyles, Typography } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'
import useTranslate from '../i18n/useTranslate'
import Panel from '../component/report/reviewer/Panel'
import DataTable from '../component/report/reviewer/DataTable'
import SearchAccordion from '../control/accordion/SearchAccordion'
import useAuthenStore from '../stores/authen/authenStore'
import moment from 'moment'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import viewerExport from '../component/report/reviewer/report'
import useSearchReviewer, {
    ReviewerSearchProps,
    ReviewerSearchForm,
    ReviewerResponseType,
    CommitteesType,
} from '../services/um/useSearchReviewer'
import useProjectType from '../services/um/lookups/useProjectType'
import { maxBy, zipObject } from 'lodash'
import { useHistory } from 'react-router'
import { convertDateToFormatEN, convertDateToFormatTH } from '../utilities/date'
import { useQueryParam } from 'use-query-params'
import { FormApi } from 'final-form'

interface ReviewerProps {}
const Reviewer: FC<ReviewerProps> = ({}) => {
    const classes = useStyles()
    const translate = useTranslate()
    const history = useHistory()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const { getReviewerData } = useSearchReviewer()
    const authenStore = useAuthenStore()
    const [dataList, setDataList] = useState<ReviewerResponseType[]>([])
    const [records, setRecords] = useState<number>(0)
    const { projectT } = useProjectType()
    const [keyword, setKeyword] = useQueryParam<string | undefined>('keyword')
    const [projectType, setProjectType] = useQueryParam<number | undefined>('projectType')
    const [startDate, setStartDate] = useQueryParam<Date | string>('startDate')
    const [endDate, setEndDate] = useQueryParam<Date | string>('endDate')
    const initialSearch: ReviewerSearchForm = {
        keyword: keyword,
        projectType: projectType,
        startDate: startDate
            ? moment(startDate).format('YYYY-MM-DD')
            : moment().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    }

    const onSubmit = async (value: ReviewerSearchForm) => {
        const param: ReviewerSearchProps = {
            userID: authenStore.profile.userId ?? 0,
            keyword: value.keyword,
            projectType: value.projectType,
            startDate: moment(value.startDate).format('YYYY-MM-DD'), //'2021-08-01',
            endDate: moment(value.endDate).format('YYYY-MM-DD'), //'2021-09-10',
        }
        setSearchParam(value)
        loadData(param)
    }

    const setSearchParam = (value: ReviewerSearchForm) => {
        setKeyword(value.keyword)
        setProjectType(value.projectType)
        setStartDate(moment(value.startDate).format('YYYY-MM-DD') ?? '')
        setEndDate(moment(value.endDate).format('YYYY-MM-DD') ?? '')
    }

    const loadData = async (param: ReviewerSearchProps) => {
        const data = await getReviewerData(param)
        setRecords(data.length)
        setDataList(data)
    }

    const initialLoad = () => {
        const param: ReviewerSearchProps = {
            userID: authenStore.profile.userId ?? 0,
            keyword: initialSearch.keyword,
            projectType: initialSearch.projectType,
            startDate: moment(initialSearch.startDate).format('YYYY-MM-DD'), //'2021-08-01',
            endDate: moment(initialSearch.endDate).format('YYYY-MM-DD'), //'2021-09-10',
        }
        setSearchParam(initialSearch)
        loadData(param)
    }

    const onReset = (form: FormApi<ReviewerSearchForm, Partial<ReviewerSearchForm>>) => {
        let param = {
            startDate: moment().subtract(1, 'year').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
        }
        form.initialize(param)
        setSearchParam(param)
    }

    const getProjectTypeName = (value?: number) => {
        let result = null
        if (value) {
            result = projectT.find((item) => item.value === projectType?.toString())?.label
        }
        return result ?? '-'
    }

    const commiteesExtract = (values: CommitteesType[]) => {
        let keyArr: string[] = []
        const nameArr = values.map((item, i) => {
            keyArr.push(`committee${i + 1}`)
            return item.name[lang]
        })
        const zip = zipObject(keyArr, nameArr)
        return zip
    }

    const laypersonExtract = (values: CommitteesType[]) => {
        let keyArr: string[] = []
        const nameArr = values.map((item, i) => {
            keyArr.push(`layperson${i + 1}`)
            return item.name[lang]
        })
        const zip = zipObject(keyArr, nameArr)
        return zip
    }

    const handleExportExcel = () => {
        let convertExportData = dataList.map((itm) => {
            const zip = commiteesExtract(itm.committees)
            const zipLay = laypersonExtract(itm.laypersons)

            return {
                projectID: itm.projectNumber,
                researcherName: itm.name[lang],
                considerType: itm.projectType.name[lang],
                projectNameTH: itm.projectName['th'],
                projectNameEN: itm.projectName['en'],
                committee: itm.committees.map((item) => item.name[lang]),
                layperson: itm.laypersons.map((item) => item.name[lang]),
                receiveDate:
                    translate.languageStore.currentLanguage === translate.languageKey.th
                        ? convertDateToFormatTH(itm.receiveDate)
                        : convertDateToFormatEN(itm.receiveDate),
                ...zip,
                ...zipLay
            }
        })

        const committeeLength = maxBy(convertExportData, (i) => i.committee.length)?.committee.length || 1
        const laypersonLength = maxBy(convertExportData, (i) => i.layperson.length)?.layperson.length || 1

        viewerExport({
            header: {
                projectType: getProjectTypeName(projectType),
                startDate: convertDate(moment(startDate).format('YYYY-MM-DD')),
                endDate: convertDate(moment(endDate).format('YYYY-MM-DD')),
                keyword: keyword ?? '-',
            },
            content: convertExportData,
            committeeLength: committeeLength,
            laypersonLength: laypersonLength,
            translate: (value: string) => translate.get(value),
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    useEffect(() => {
        if (authenStore.profile.userId) {
            initialLoad()
        }
    }, [authenStore.profile.userId])

    const onBack = () => {
        history.push('/dashboard')
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion title={translate.get('UM.REVIEWER.HEADER')} isBack={true} onClickBack={onBack}>
                    <Panel
                        onSubmit={onSubmit}
                        initialSearch={{ keyword, projectType, startDate, endDate }}
                        onReset={onReset}
                    />
                </SearchAccordion>
                <Grid container justify="space-between" alignItems="center" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<GetAppRoundedIcon style={{ color: '#DB5F8E' }} />}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <DataTable data={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}
export default Reviewer

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    container: {
        marginTop: '20px',
        width: '100%',
    },
}))
